/* eslint-disable @typescript-eslint/no-explicit-any */
import * as signalR from '@microsoft/signalr';
import { getSignalRConfig } from '@/configs/SIGNALR_CONFIG';
import { tokenService } from '../token/token-service';
import { useAuthStore } from '@/store/useAuthStore';
import { threadSDK, threadThaoLuanSDK } from '@/types/social/thread';
import { logDbRestoreTrace } from '@/utils/dbRestoreSync';
import { logCopyDataTrace } from '@/utils/copyDataProgressSync';

export interface SignalRNotificationMessage {
    title: string;
    body: string;
    type: string; // info, success, warning, error
    data: Record<string, any>;
    timestamp: string;
    imageUrl?: string;
    clickAction?: string;
    badge?: number;
}

// Thread/Comment SignalR event types
export interface ThreadEventData {
    thread: threadSDK;
    action: 'create' | 'update' | 'delete' | 'reaction';
}

export interface CommentEventData {
    comment: threadThaoLuanSDK;
    threadID: string;
    action: 'create' | 'update' | 'delete' | 'reaction';
}

class SignalRService {
    private connection: signalR.HubConnection | null = null;
    private isConnected = false;
    private isConnecting = false;
    private isDisconnecting = false;
    private connectPromise: Promise<void> | null = null;
    private disconnectPromise: Promise<void> | null = null;

    // Track joined groups to auto rejoin after reconnect
    private joinedTicketGroups: Set<string> = new Set();
    private joinedGroups: Set<string> = new Set(); // Track regular groups (including tenant group)

    // Heartbeat interval
    private heartbeatTimer: any = null;

    // Recovery after withAutomaticReconnect exhausts (or network back online)
    private recoveryTimer: ReturnType<typeof setTimeout> | null = null;
    private recoveryAttempt = 0;
    private intentionalStop = false;
    private onlineListenerAttached = false;

    // Lưu reference đến các handler functions để có thể off chính xác
    private serverEventHandlers: Map<string, Array<(...args: any[]) => void>> = new Map();

    // Track connection instance để chỉ setup handlers một lần cho mỗi connection
    private currentConnectionId: string | null = null;

    private reconnectAttempts = 0;
    private maxReconnectAttempts = 5;
    private reconnectDelay = 1000;

    private onConnectionStateChangedHandlers: Set<(isConnected: boolean) => void> = new Set();

    // 🔥 THÊM: Callbacks để thông báo khi cần re-register handlers (reconnect)
    private onReconnectCallbacks: Set<() => void> = new Set();

    // Event handlers - chỉ giữ các event có trong setupEventHandlers
    private onReloadEmployee: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadDepartment: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadPosition: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadRoleGroup: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadPeriod: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadUnit: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadRest: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadOrgData: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadDbRestore: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadCopyData: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onReloadBulkEmployeeProgress: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onFishbonePresence: Set<(payload: unknown) => void> = new Set();
    private onFishboneUpdated: Set<(payload: unknown) => void> = new Set();
    private onStrategyMapPresence: Set<(payload: unknown) => void> = new Set();
    private onStrategyMapUpdated: Set<(payload: unknown) => void> = new Set();
    private dynamicEventHandlers = new Map<string, Set<(payload: unknown) => void>>();
    private onReloadFishboneSave: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onMessageHandlers: Set<(fromUserId: string, message: string) => void> = new Set();
    private onPrivateMessageHandlers: Set<(fromUserId: string, message: string) => void> = new Set();
    private onGroupMessageHandlers: Set<(groupName: string, fromUserId: string, message: string) => void> = new Set();
    private onNotificationHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onSystemMessageHandlers: Set<(message: string) => void> = new Set();
    private onUserDisconnectedHandlers: Set<(data: { userId: string; timestamp: string }) => void> = new Set();
    private onErrorHandlers: Set<(errorMessage: string) => void> = new Set();

    // Thread/Comment event handlers - nhận SignalRNotificationMessage giống ReceiveNotification
    private onThreadCreatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadUpdatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadDeletedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadReactionUpdatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onCommentCreatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onCommentUpdatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onCommentDeletedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onCommentReactionUpdatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();

    // ThreadPage event handlers
    private onThreadPageCreatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPageUpdatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPageDeletedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPageUserJoinedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPageUserLeftHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPageRequestApprovedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPageRequestRejectedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPageOwnershipTransferredHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();

    // ThreadPoll event handlers
    private onThreadPollCreatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPollUpdatedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPollDeletedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();
    private onThreadPollVotedHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();

    // Quiz event handlers
    private onQuizHandlers: Set<(notification: SignalRNotificationMessage) => void> = new Set();

    // License reload handlers
    private onReloadLicense: Set<(notification: SignalRNotificationMessage) => void> = new Set();

    // Config reload handlers
    private onReloadConfig: Set<(notification: SignalRNotificationMessage) => void> = new Set();

    constructor() {
        // Không await trong constructor, sẽ initialize khi connect
    }

    // 🧩 Khởi tạo kết nối SignalR
    private async initializeConnection(): Promise<void> {
        if (this.connection && this.isConnected) {
            return;
        }


        const config = getSignalRConfig();
        logDbRestoreTrace('4-FE-initConnection', {
            hubUrl: config.hubUrl,
            authEndpoint: config.authEndpoint,
        });

        // Lấy token ban đầu để cache
        let cachedToken: string | null = null;
        try {
            cachedToken = await tokenService.getValidToken();
        } catch (error) {
            console.warn('[SignalR] Could not get initial token:', error);
        }

        this.connection = new signalR.HubConnectionBuilder()
            .withUrl(config.hubUrl, {
                withCredentials: true,
                skipNegotiation: false,
                // WebSocket: 1 kết nối duplex, không giữ GET pending → không block chuyển route
                // LongPolling: fallback khi WS không khả dụng (vẫn qua BASE_URL, không qua localhost proxy)
                transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
                accessTokenFactory: async () => {
                    try {
                        // Luôn lấy token mới nhất (SignalR hỗ trợ async accessTokenFactory)
                        const validToken = await tokenService.getValidToken();

                        if (!validToken) {
                            console.warn('[SignalR] No valid token available');
                            // Không trả '' — tránh negotiate 401 liên tục; dùng cache nếu còn
                            if (cachedToken) return cachedToken;
                            throw new Error('No valid access token for SignalR');
                        }

                        // Update cached token
                        cachedToken = validToken;
                        return validToken;
                    } catch (error) {
                        console.error('[SignalR] Error getting token:', error);
                        if (cachedToken) return cachedToken;
                        throw error instanceof Error
                            ? error
                            : new Error('No valid access token for SignalR');
                    }
                },
                timeout: 30000, // 30 seconds for better UX
                headers: config.authEndpoint ? { 'X-Auth-Endpoint': config.authEndpoint } : {},
            })
            // Automatic reconnect với exponential backoff
            .withAutomaticReconnect({
                nextRetryDelayInMilliseconds: (retryContext) => {
                    // Custom logic thay vì array cố định
                    if (retryContext.previousRetryCount === 0) return 0;        // Ngay lập tức
                    if (retryContext.previousRetryCount === 1) return 2000;     // 2 giây
                    if (retryContext.previousRetryCount === 2) return 5000;    // 5 giây
                    if (retryContext.previousRetryCount === 3) return 10000;    // 10 giây
                    if (retryContext.previousRetryCount === 4) return 15000;    // 15 giây
                    if (retryContext.previousRetryCount === 5) return 20000;    // 20 giây
                    if (retryContext.previousRetryCount === 6) return 30000;    // 30 giây

                    // Hết budget auto-reconnect → schedule recovery dài hơn + chờ online
                    console.error('[SignalR] Max reconnect attempts reached; scheduling recovery');
                    this.scheduleRecoveryReconnect(30000);
                    return null;
                }
            })
            // Logging theo environment
            .configureLogging(
                process.env.NODE_ENV === 'development' && false
                    ? signalR.LogLevel.Debug
                    : signalR.LogLevel.Warning // Production: chỉ log warnings/errors
            )
            .build();

        // Increase timeouts to tolerate idle periods
        this.connection.serverTimeoutInMilliseconds = 30 * 60 * 1000; // restore DB có thể > 2 phút
        this.connection.keepAliveIntervalInMilliseconds = 15000; // 15s

        this.setupConnectionEvents();
        this.setupServerEventHandlers();
        this.ensureOnlineRecoveryListener();
    }

    // ⚙️ Gắn server event handlers - gọi tất cả registered handlers
    // Note: Event handlers được đăng ký tập trung trong RegisterHandlers.ts
    private setupServerEventHandlers() {
        if (!this.connection) {
            console.warn('[SignalR] Cannot setup handlers: connection is null');
            return;
        }

        const connectionId = this.connection.connectionId;

        // Guard: nếu đã setup rồi cho connection này (kể cả khi connectionId null ở lần gọi trước)
        // thì skip để tránh bind duplicate listeners. Connection.on() không tự dedupe —
        // mỗi lần gọi sẽ add thêm 1 listener mới.
        if (this.serverEventHandlers.size > 0 && this.currentConnectionId === connectionId) {
            logDbRestoreTrace('3-skip-hubHandlers-alreadySetup', {
                connectionId,
                registeredEvents: Array.from(this.serverEventHandlers.keys()),
            });
            return;
        }

        // Nếu connection chưa start (connectionId = null), vẫn setup handlers
        // Handlers sẽ được gọi khi connection start và events đến

        // Remove existing listeners bằng cách off từng handler cụ thể (tránh off tất cả)
        this.serverEventHandlers.forEach((handlers, eventName) => {
            handlers.forEach(handler => {
                try {
                    this.connection?.off(eventName, handler);
                } catch (error) {
                    console.warn(`[SignalR] Error removing handler for ${eventName}:`, error);
                }
            });
        });
        this.serverEventHandlers.clear();

        // Helper function để register event handler và lưu reference
        const registerHandler = (eventName: string, handler: (...args: any[]) => void) => {
            this.connection?.on(eventName, handler);
            if (!this.serverEventHandlers.has(eventName)) {
                this.serverEventHandlers.set(eventName, []);
            }
            this.serverEventHandlers.get(eventName)!.push(handler);
        };

        // Server events - call all registered handlers
        registerHandler('reloadDepartment', (notification: SignalRNotificationMessage) => {
            this.onReloadDepartment.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadDepartment handler:', error); }
            });
        });
        registerHandler('reloadEmployee', (notification: SignalRNotificationMessage) => {
            this.onReloadEmployee.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadEmployee handler:', error); }
            });
        });
        registerHandler('reloadPosition', (notification: SignalRNotificationMessage) => {
            this.onReloadPosition.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadPosition handler:', error); }
            });
        });
        registerHandler('reloadRoleGroup', (notification: SignalRNotificationMessage) => {
            this.onReloadRoleGroup.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadRoleGroup handler:', error); }
            });
        });
        registerHandler('reloadOrgData', (notification: SignalRNotificationMessage) => {
            console.log('[SignalR][reloadOrgData] notification', notification);
            this.onReloadOrgData.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadOrgData handler:', error); }
            });
        });
        const invokeHandlerSet = async (
            handlers: Set<(notification: SignalRNotificationMessage) => void | Promise<void>>,
            notification: SignalRNotificationMessage,
            label: string,
        ) => {
            for (const handler of handlers) {
                try {
                    await Promise.resolve(handler(notification));
                } catch (error) {
                    console.error(`[SignalR][${label}] handler error:`, error);
                }
            }
        };

        const invokeReloadRest = async (source: string, notification: SignalRNotificationMessage) => {
            const handlerCount = this.onReloadRest.size;
            console.log(`[SignalR][${source}] → invokeReloadRest`, {
                handlerCount,
                body: (notification as any)?.body ?? (notification as any)?.Body,
            });
            await invokeHandlerSet(this.onReloadRest, notification, `${source}/reloadRest`);
            console.log(`[SignalR][${source}] ← invokeReloadRest done`);
        };

        const handleReloadPeriod = async (notification: SignalRNotificationMessage) => {
            const body = (notification as any)?.body ?? (notification as any)?.Body;
            console.log('[SignalR][reloadPeriod] received', {
                body,
                periodHandlerCount: this.onReloadPeriod.size,
            });
            await invokeHandlerSet(this.onReloadPeriod, notification, 'reloadPeriod');
            console.log('[SignalR][reloadPeriod] onReloadPeriod done → chain reloadRest');
            await invokeReloadRest('reloadPeriod', notification);
            console.log('[SignalR][reloadPeriod] flow complete');
        };
        registerHandler('reloadPeriod', handleReloadPeriod);
        registerHandler('reloadperiod', handleReloadPeriod);

        const handleReloadUnit = async (notification: SignalRNotificationMessage) => {
            const body = (notification as any)?.body ?? (notification as any)?.Body;
            console.log('[SignalR][reloadUnit] received', {
                body,
                unitHandlerCount: this.onReloadUnit.size,
            });
            await invokeHandlerSet(this.onReloadUnit, notification, 'reloadUnit');
            console.log('[SignalR][reloadUnit] onReloadUnit done → chain reloadRest');
            await invokeReloadRest('reloadUnit', notification);
            console.log('[SignalR][reloadUnit] flow complete');
        };
        registerHandler('reloadUnit', handleReloadUnit);
        registerHandler('reloadunit', handleReloadUnit);

        const handleReloadDbRestore = (notification: SignalRNotificationMessage) => {
            const body = String((notification as { body?: string; Body?: string }).body
              ?? (notification as { body?: string; Body?: string }).Body ?? '').toLowerCase();
            const data = (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).data
              ?? (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).Data;
            const dataPhase = String(data?.phase ?? data?.Phase ?? '').toLowerCase();
            const isComplete = body === 'complete' || dataPhase === 'complete';

            logDbRestoreTrace(isComplete ? '10-FE-hubEvent-reloadDbRestore-COMPLETE' : '10-FE-hubEvent-reloadDbRestore', {
                notification,
                body,
                dataPhase,
                handlerCount: this.onReloadDbRestore.size,
                connectionId: this.connection?.connectionId,
                connectionState: this.connection?.state,
            });
            if (this.onReloadDbRestore.size === 0) {
                logDbRestoreTrace('10-FE-hubEvent-reloadDbRestore-NO-HANDLER', { body, dataPhase });
            }
            this.onReloadDbRestore.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('[DbRestoreSignalR] handler error:', error); }
            });
        };
        registerHandler('reloadDbRestore', handleReloadDbRestore);
        registerHandler('reloaddbrestore', handleReloadDbRestore);
        registerHandler('ReloadDbRestore', handleReloadDbRestore);
        logDbRestoreTrace('3-FE-hubListeners-registered', {
            events: ['reloadDbRestore', 'reloaddbrestore', 'ReloadDbRestore'],
            onReloadDbRestoreHandlerCount: this.onReloadDbRestore.size,
            connectionId: this.connection?.connectionId ?? '(not started)',
        });

        // Tiến độ xử lý nhân viên hàng loạt (đặt lại mật khẩu...) - gửi riêng cho user thao tác.
        const handleBulkEmployeeProgress = (notification: SignalRNotificationMessage) => {
            this.onReloadBulkEmployeeProgress.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('[BulkEmployeeProgress] handler error:', error); }
            });
        };
        registerHandler('reloadBulkEmployeeProgress', handleBulkEmployeeProgress);
        registerHandler('reloadbulkemployeeprogress', handleBulkEmployeeProgress);
        registerHandler('ReloadBulkEmployeeProgress', handleBulkEmployeeProgress);

        const handleReloadCopyData = (notification: SignalRNotificationMessage) => {
            logCopyDataTrace('hubEvent-reloadCopyData', {
                notification,
                handlerCount: this.onReloadCopyData.size,
                connectionId: this.connection?.connectionId,
            });
            this.onReloadCopyData.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('[CopyDataProgress] handler error:', error); }
            });
        };
        registerHandler('reloadCopyData', handleReloadCopyData);
        registerHandler('reloadcopydata', handleReloadCopyData);
        registerHandler('ReloadCopyData', handleReloadCopyData);
        registerHandler('fishbonePresence', (payload: unknown) => {
            this.onFishbonePresence.forEach(handler => {
                try { handler(payload); } catch (error) { console.error('Error in fishbonePresence handler:', error); }
            });
        });
        const handleFishboneUpdated = (payload: unknown) => {
            this.onFishboneUpdated.forEach(handler => {
                try { handler(payload); } catch (error) { console.error('Error in fishboneUpdated handler:', error); }
            });
        };
        registerHandler('fishboneUpdated', handleFishboneUpdated);
        registerHandler('fishboneupdated', handleFishboneUpdated);
        registerHandler('FishboneUpdated', handleFishboneUpdated);
        const handleStrategyMapPresence = (payload: unknown) => {
            this.onStrategyMapPresence.forEach(handler => {
                try { handler(payload); } catch (error) { console.error('Error in strategyMapPresence handler:', error); }
            });
        };
        registerHandler('strategyMapPresence', handleStrategyMapPresence);
        registerHandler('strategymappresence', handleStrategyMapPresence);
        const handleStrategyMapUpdated = (payload: unknown) => {
            this.onStrategyMapUpdated.forEach(handler => {
                try { handler(payload); } catch (error) { console.error('Error in strategyMapUpdated handler:', error); }
            });
        };
        registerHandler('strategyMapUpdated', handleStrategyMapUpdated);
        registerHandler('strategymapupdated', handleStrategyMapUpdated);
        ['buoc6Presence', 'buoc6Updated', 'buoc7Presence', 'buoc7Updated', 'openTabBeat', 'openTabBye'].forEach((eventName) => {
            registerHandler(eventName, (payload: unknown) => {
                this.dynamicEventHandlers.get(eventName)?.forEach((handler) => {
                    try { handler(payload); } catch (error) { console.error(`Error in ${eventName} handler:`, error); }
                });
            });
        });
        const handleReloadFishboneSave = (notification: SignalRNotificationMessage) => {
            this.onReloadFishboneSave.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadFishboneSave handler:', error); }
            });
        };
        registerHandler('reloadFishboneSave', handleReloadFishboneSave);
        registerHandler('reloadfishbonesave', handleReloadFishboneSave);
        registerHandler('ReloadFishboneSave', handleReloadFishboneSave);
        logCopyDataTrace('hubListeners-registered', {
            events: ['reloadCopyData', 'reloadcopydata', 'ReloadCopyData'],
            handlerCount: this.onReloadCopyData.size,
        });

        registerHandler('ReceiveMessage', (fromUserId: string, message: string) => {
            console.log('ReceiveMessage =>>>>', fromUserId, message);
            this.onMessageHandlers.forEach(handler => {
                try { handler(fromUserId, message); } catch (error) { console.error('Error in ReceiveMessage handler:', error); }
            });
        });
        // Handle private messages - register both cases to be safe
        const handlePrivateMessage = (fromUserId: string, message: string) => {
            this.onPrivateMessageHandlers.forEach(handler => {
                try { handler(fromUserId, message); } catch (error) { console.error('Error in ReceivePrivateMessage handler:', error); }
            });
            // Also trigger general message handlers for backward compatibility
            this.onMessageHandlers.forEach(handler => {
                try { handler(fromUserId, message); } catch (error) { console.error('Error in ReceiveMessage handler:', error); }
            });
        };
        // Register with both PascalCase and lowercase to handle server variations
        registerHandler('ReceivePrivateMessage', handlePrivateMessage);
        registerHandler('receiveprivatemessage', handlePrivateMessage);

        // Handle group messages - register both cases to be safe
        const handleGroupMessage = (groupName: string, fromUserId: string, message: string) => {
            this.onGroupMessageHandlers.forEach(handler => {
                try { handler(groupName, fromUserId, message); } catch (error) { console.error('Error in ReceiveGroupMessage handler:', error); }
            });
            // Also trigger general message handlers for backward compatibility
            this.onMessageHandlers.forEach(handler => {
                try { handler(fromUserId, message); } catch (error) { console.error('Error in ReceiveMessage handler:', error); }
            });
        };
        // Register with both PascalCase and lowercase to handle server variations
        registerHandler('ReceiveGroupMessage', handleGroupMessage);
        registerHandler('receivegroupmessage', handleGroupMessage);

        const looksLikeFishboneSaveNotification = (notification: SignalRNotificationMessage): boolean => {
            const title = String(notification?.title ?? '').toLowerCase();
            const type = String(notification?.type ?? '').toLowerCase();
            const data = (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).data
              ?? (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).Data
              ?? {};
            const eventName = String(
              (notification as { eventName?: string; EventName?: string }).eventName
              ?? (notification as { eventName?: string; EventName?: string }).EventName
              ?? data.eventName
              ?? data.EventName
              ?? ''
            ).toLowerCase();
            const stepKey = String(data.stepKey ?? data.StepKey ?? '').toLowerCase();
            return (
              eventName === 'reloadfishbonesave'
              || type.includes('fishbonesave')
              || title.includes('lưu xương cá')
              || title.includes('luu xuong ca')
              || ['objectives', 'objective', 'deleteobjectives', 'deletekpis', 'plan', 'weight'].includes(stepKey)
            );
        };

        const looksLikeCopyDataNotification = (notification: SignalRNotificationMessage): boolean => {
            const title = String(notification?.title ?? '').toLowerCase();
            const type = String(notification?.type ?? '').toLowerCase();
            const data = (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).data
              ?? (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).Data
              ?? {};
            return (
              type.includes('copydata') ||
              type.includes('copy-data') ||
              title.includes('copy data') ||
              title.includes('copy dữ liệu') ||
              title.includes('chuyển dữ liệu') ||
              title.includes('chuyen du lieu') ||
              Boolean(data.stepKey ?? data.StepKey) ||
              Boolean(
                (data.jobId ?? data.JobId ?? data.runId ?? data.RunId) &&
                (data.tuNam ?? data.TuNam ?? data.denNam ?? data.DenNam ?? data.stepKey ?? data.StepKey)
              )
            );
        };

        registerHandler('ReceiveNotification', (notification: SignalRNotificationMessage) => {
            const title = String(notification?.title ?? '').toLowerCase();
            const body = String(notification?.body ?? '').toLowerCase();
            const data = (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).data
              ?? (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).Data;
            const dataPhase = String(data?.phase ?? data?.Phase ?? '').toLowerCase();

            if (looksLikeFishboneSaveNotification(notification)) {
                handleReloadFishboneSave(notification);
                return;
            }

            if (looksLikeCopyDataNotification(notification)) {
                logCopyDataTrace('ReceiveNotification-copyData', { title, body, dataPhase });
                handleReloadCopyData(notification);
                // Không forward sang onNotification — tránh invalidate notifications / refetch badge mỗi bước progress.
                return;
            }

            const isDbRestore = title.includes('database restore')
              || body === 'start'
              || body === 'complete'
              || body === 'progress'
              || dataPhase === 'start'
              || dataPhase === 'complete'
              || dataPhase === 'progress';
            if (isDbRestore) {
                logDbRestoreTrace(body === 'complete' || dataPhase === 'complete'
                  ? '13-FE-ReceiveNotification-dbRestore-COMPLETE'
                  : '13-FE-ReceiveNotification-dbRestore', {
                  notification,
                  title,
                  body,
                  dataPhase,
                  handlerCount: this.onReloadDbRestore.size,
                });
                // BE SendToTenant có thể gửi qua ReceiveNotification thay vì reloadDbRestore
                handleReloadDbRestore(notification);
                return;
            }
            this.onNotificationHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ReceiveNotification handler:', error); }
            });
        });
        registerHandler('receivenotification', (notification: SignalRNotificationMessage) => {
            const title = String(notification?.title ?? '').toLowerCase();
            const body = String(notification?.body ?? '').toLowerCase();
            const data = (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).data
              ?? (notification as { data?: Record<string, unknown>; Data?: Record<string, unknown> }).Data;
            const dataPhase = String(data?.phase ?? data?.Phase ?? '').toLowerCase();

            if (looksLikeFishboneSaveNotification(notification)) {
                handleReloadFishboneSave(notification);
                return;
            }

            if (looksLikeCopyDataNotification(notification)) {
                handleReloadCopyData(notification);
                return;
            }

            const isDbRestore = title.includes('database restore')
              || body === 'start'
              || body === 'complete'
              || body === 'progress'
              || dataPhase === 'start'
              || dataPhase === 'complete'
              || dataPhase === 'progress';
            if (isDbRestore) {
                handleReloadDbRestore(notification);
                return;
            }
            this.onNotificationHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ReceiveNotification handler:', error); }
            });
        });
        // Handle system messages - register both cases to be safe
        const handleSystemMessage = (message: string) => {
            this.onSystemMessageHandlers.forEach(handler => {
                try { handler(message); } catch (error) { console.error('Error in ReceiveSystemMessage handler:', error); }
            });
        };
        // Register with both PascalCase and lowercase to handle server variations
        registerHandler('ReceiveSystemMessage', handleSystemMessage);
        registerHandler('receivesystemmessage', handleSystemMessage);
        registerHandler('UserDisconnected', (data: { UserId: string; Timestamp: string }) => {
            this.onUserDisconnectedHandlers.forEach(handler => {
                try { handler({ userId: data.UserId, timestamp: data.Timestamp }); } catch (error) { console.error('Error in UserDisconnected handler:', error); }
            });
        });
        registerHandler('Error', (errorMessage: string) => {
            this.onErrorHandlers.forEach(handler => {
                try { handler(errorMessage); } catch (error) { console.error('Error in Error handler:', error); }
            });
        });

        // Thread/Comment events - nhận SignalRNotificationMessage giống ReceiveNotification
        registerHandler('ThreadCreated', (notification: SignalRNotificationMessage) => {
            this.onThreadCreatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadCreated handler:', error); }
            });
        });
        registerHandler('ThreadUpdated', (notification: SignalRNotificationMessage) => {
            this.onThreadUpdatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadUpdated handler:', error); }
            });
        });
        registerHandler('ThreadDeleted', (notification: SignalRNotificationMessage) => {
            this.onThreadDeletedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadDeleted handler:', error); }
            });
        });
        registerHandler('ThreadReactionUpdated', (notification: SignalRNotificationMessage) => {
            this.onThreadReactionUpdatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadReactionUpdated handler:', error); }
            });
        });
        registerHandler('CommentCreated', (notification: SignalRNotificationMessage) => {
            this.onCommentCreatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in CommentCreated handler:', error); }
            });
        });
        registerHandler('CommentUpdated', (notification: SignalRNotificationMessage) => {
            this.onCommentUpdatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in CommentUpdated handler:', error); }
            });
        });
        registerHandler('CommentDeleted', (notification: SignalRNotificationMessage) => {
            this.onCommentDeletedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in CommentDeleted handler:', error); }
            });
        });
        registerHandler('CommentReactionUpdated', (notification: SignalRNotificationMessage) => {
            this.onCommentReactionUpdatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in CommentReactionUpdated handler:', error); }
            });
        });

        // ThreadPage events
        registerHandler('ThreadPageCreated', (notification: SignalRNotificationMessage) => {
            this.onThreadPageCreatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageCreated handler:', error); }
            });
        });
        registerHandler('ThreadPageUpdated', (notification: SignalRNotificationMessage) => {
            this.onThreadPageUpdatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageUpdated handler:', error); }
            });
        });
        registerHandler('ThreadPageDeleted', (notification: SignalRNotificationMessage) => {
            this.onThreadPageDeletedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageDeleted handler:', error); }
            });
        });
        registerHandler('ThreadPageUserJoined', (notification: SignalRNotificationMessage) => {
            this.onThreadPageUserJoinedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageUserJoined handler:', error); }
            });
        });
        registerHandler('ThreadPageUserLeft', (notification: SignalRNotificationMessage) => {
            this.onThreadPageUserLeftHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageUserLeft handler:', error); }
            });
        });
        registerHandler('ThreadPageRequestApproved', (notification: SignalRNotificationMessage) => {
            this.onThreadPageRequestApprovedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageRequestApproved handler:', error); }
            });
        });
        registerHandler('ThreadPageRequestRejected', (notification: SignalRNotificationMessage) => {
            this.onThreadPageRequestRejectedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageRequestRejected handler:', error); }
            });
        });
        registerHandler('ThreadPageOwnershipTransferred', (notification: SignalRNotificationMessage) => {
            this.onThreadPageOwnershipTransferredHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPageOwnershipTransferred handler:', error); }
            });
        });

        // ThreadPoll events
        registerHandler('ThreadPollCreated', (notification: SignalRNotificationMessage) => {
            this.onThreadPollCreatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPollCreated handler:', error); }
            });
        });
        registerHandler('ThreadPollUpdated', (notification: SignalRNotificationMessage) => {
            this.onThreadPollUpdatedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPollUpdated handler:', error); }
            });
        });
        registerHandler('ThreadPollDeleted', (notification: SignalRNotificationMessage) => {
            this.onThreadPollDeletedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPollDeleted handler:', error); }
            });
        });
        registerHandler('ThreadPollVoted', (notification: SignalRNotificationMessage) => {
            this.onThreadPollVotedHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in ThreadPollVoted handler:', error); }
            });
        });

        // Quiz events
        registerHandler('reloadquiziframe', (notification: SignalRNotificationMessage) => {
            this.onQuizHandlers.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadquiziframe handler:', error); }
            });
        });

        // License reload event
        registerHandler('reloadLicense', (notification: SignalRNotificationMessage) => {
            this.onReloadLicense.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadLicense handler:', error); }
            });
        });

        // Config reload event
        registerHandler('reloadConfig', (notification: SignalRNotificationMessage) => {
            console.log('[SignalR][reloadConfig] received', notification);
            this.onReloadConfig.forEach(handler => {
                try { handler(notification); } catch (error) { console.error('Error in reloadConfig handler:', error); }
            });
        });

        // Lưu connection ID để track — set cả khi null để guard skip ở đầu function
        // hoạt động đúng cho cả trường hợp setup trước khi connection start.
        this.currentConnectionId = connectionId ?? null;

    }

    // Connection events
    private setupConnectionEvents() {
        if (!this.connection) return;

        this.connection.onclose((error) => {
            logDbRestoreTrace('7-FE-connection-closed', error?.message ?? error);
            console.warn('🔴 SignalR connection closed:', error);
            this.isConnected = false;
            this.stopHeartbeat();
            // Reset connection ID để có thể setup lại handlers khi reconnect
            this.currentConnectionId = null;
            this.onConnectionStateChangedHandlers.forEach(handler => {
                try { handler(false); } catch (error) { console.error('Error in ConnectionStateChanged handler:', error); }
            });
            // withAutomaticReconnect đã dừng (hoặc stop chủ động) → recovery nếu vẫn còn session
            if (!this.intentionalStop && useAuthStore.getState().accessToken) {
                this.scheduleRecoveryReconnect(2000);
            }
        });

        this.connection.onreconnecting((error) => {
            logDbRestoreTrace('7-FE-connection-reconnecting', error?.message ?? error);
            console.warn('🟠 SignalR reconnecting:', error);
            this.isConnected = false;
            this.stopHeartbeat();
            this.onConnectionStateChangedHandlers.forEach(handler => {
                try { handler(false); } catch (error) { console.error('Error in ConnectionStateChanged handler:', error); }
            });
        });

        this.connection.onreconnected((connectionId) => {
            logDbRestoreTrace('7-FE-connection-reconnected', { connectionId });
            this.isConnected = true;
            this.reconnectAttempts = 0;
            this.recoveryAttempt = 0;
            this.clearRecoveryTimer();

            // QUAN TRỌNG: Setup handlers lại sau khi reconnect
            // Đảm bảo handlers luôn được setup khi connection active
            this.setupServerEventHandlers();

            this.startHeartbeat();

            // 🔥 QUAN TRỌNG: Gọi reconnect callbacks để re-register handlers
            this.onReconnectCallbacks.forEach(callback => {
                try {
                    callback();
                } catch (error) {
                    console.error('Error in reconnect callback:', error);
                }
            });

            this.onConnectionStateChangedHandlers.forEach(handler => {
                try { handler(true); } catch (error) { console.error('Error in ConnectionStateChanged handler:', error); }
            });

            // Auto rejoin previously joined groups
            try {
                // Rejoin tenant group
                const tenantID = useAuthStore.getState().tenantID;
                if (tenantID) {
                    this.autoJoinTenantGroup(tenantID).catch(err => {
                        console.error('❌ [SignalR] Failed to rejoin tenant group after reconnect:', err);
                    });
                }

                // Rejoin ticket groups
                this.joinedTicketGroups.forEach(async (ticketId) => {
                    try {
                        await this.safeInvoke('JoinTicketGroup', ticketId);
                    } catch (err) {
                        console.warn(`❌ [SignalR] Failed to rejoin ticket group ${ticketId} after reconnect:`, err);
                    }
                });

                // Rejoin other groups
                this.joinedGroups.forEach(async (groupName) => {
                    try {
                        await this.safeInvoke('JoinGroup', groupName);
                    } catch (err) {
                        console.warn(`❌ [SignalR] Failed to rejoin group ${groupName} after reconnect:`, err);
                    }
                });
            } catch { /* ignore */ }
        });
    }

    // 🚀 Kết nối an toàn, chống đua trạng thái
    public async connect(): Promise<void> {
        logDbRestoreTrace('4-FE-connect-start', {
            hubUrl: getSignalRConfig().hubUrl,
            state: this.connection?.state ?? 'no-connection',
        });

        if (!useAuthStore.getState().accessToken) {
            console.warn('[SignalR] Skip connect: no accessToken');
            return;
        }

        this.intentionalStop = false;
        this.clearRecoveryTimer();

        if (!this.connection) await this.initializeConnection();

        const conn = this.connection!;

        // Nếu đang disconnect, đợi hoàn thành
        if (this.isDisconnecting && this.disconnectPromise) {
            await this.disconnectPromise;
        }

        // Nếu đã connected, đảm bảo handlers được setup rồi return
        if (conn.state === signalR.HubConnectionState.Connected) {
            this.isConnected = true;
            // QUAN TRỌNG: Setup handlers nếu chưa được setup
            // (tránh trường hợp connection tồn tại nhưng handlers chưa setup)
            this.setupServerEventHandlers();
            return;
        }

        // Nếu đang connecting, đợi hoàn thành
        if (this.isConnecting && this.connectPromise) {
            await this.connectPromise;
            return;
        }

        this.isConnecting = true;
        this.connectPromise = (async () => {
            try {
                // Kiểm tra lại state sau khi đợi
                if (conn.state === signalR.HubConnectionState.Connected) {
                    this.isConnected = true;
                    return;
                }

                // Nếu đang disconnect, không start
                if (this.isDisconnecting) {
                    throw new Error('Cannot start connection while disconnecting');
                }

                switch (conn.state) {
                    case signalR.HubConnectionState.Disconnected:
                        await conn.start();
                        break;

                    case signalR.HubConnectionState.Disconnecting:
                        await this.waitForState(signalR.HubConnectionState.Disconnected, 2000);
                        // Kiểm tra lại sau khi đợi
                        if (this.isDisconnecting) {
                            throw new Error('Connection was stopped during wait');
                        }
                        await conn.start();
                        break;

                    case signalR.HubConnectionState.Connecting:
                        await this.waitUntilNot(signalR.HubConnectionState.Connecting, 2000);
                        // Sau khi đợi, kiểm tra state và start nếu cần
                        if (!this.isDisconnecting) {
                            // Kiểm tra state sau khi đợi - sử dụng type assertion
                            const currentState = conn.state as signalR.HubConnectionState;
                            if (currentState === signalR.HubConnectionState.Disconnected) {
                                await conn.start();
                            }
                            // Nếu state là Connected hoặc Reconnecting, không cần làm gì
                        }
                        break;

                    default:
                        // Already connected or reconnecting
                        break;
                }

                // Kiểm tra state sau khi xử lý - sử dụng type assertion
                const finalState = conn.state as signalR.HubConnectionState;
                this.isConnected = finalState === signalR.HubConnectionState.Connected;
                if (this.isConnected) {
                    this.reconnectAttempts = 0;
                    this.recoveryAttempt = 0;
                    this.clearRecoveryTimer();

                    logDbRestoreTrace('5-FE-connected', {
                        connectionId: this.connection?.connectionId,
                        tenantID: useAuthStore.getState().tenantID,
                        profileId: useAuthStore.getState().profile?.id,
                        onReloadDbRestoreHandlerCount: this.onReloadDbRestore.size,
                    });

                    // QUAN TRỌNG: Setup handlers SAU khi connect thành công
                    // Đảm bảo handlers đã được đăng ký vào Set trước đó
                    this.setupServerEventHandlers();

                    this.onConnectionStateChangedHandlers.forEach(handler => {
                        try { handler(true); } catch (error) { console.error('Error in ConnectionStateChanged handler:', error); }
                    });
                    // Start heartbeat
                    this.startHeartbeat();

                    // Auto join tenant group
                    const tenantID = useAuthStore.getState().tenantID;
                    if (tenantID) {
                        this.autoJoinTenantGroup(tenantID)
                            .then(() => {
                                logDbRestoreTrace('6-FE-joinTenantGroup-ok', {
                                    tenantID,
                                    connectionId: this.connection?.connectionId,
                                });
                            })
                            .catch(err => {
                                logDbRestoreTrace('6-FE-joinTenantGroup-fail', { tenantID, error: err });
                                console.error(`❌ [SignalR] Failed to auto join tenant group ${tenantID}:`, err);
                            });
                    } else {
                        logDbRestoreTrace('6-FE-joinTenantGroup-skip', 'tenantID rỗng');
                        console.warn('⚠️ [SignalR] No tenantID found, cannot join tenant group');
                    }
                } else {
                    // Nếu không connected, có thể đang trong quá trình connecting/reconnecting
                    this.onConnectionStateChangedHandlers.forEach(handler => {
                        try { handler(false); } catch (error) { console.error('Error in ConnectionStateChanged handler:', error); }
                    });
                }
            } catch (error) {
                logDbRestoreTrace('5-FE-connect-fail', error);
                console.error('❌ SignalR connection failed:', error);
                this.isConnected = false;
                this.onConnectionStateChangedHandlers.forEach(handler => {
                    try { handler(false); } catch (error) { console.error('Error in ConnectionStateChanged handler:', error); }
                });
                if (!this.intentionalStop && useAuthStore.getState().accessToken) {
                    this.scheduleRecoveryReconnect(5000);
                }
                throw error;
            } finally {
                this.isConnecting = false;
                this.connectPromise = null;
            }
        })();

        await this.connectPromise;
    }

    // 🛑 Ngắt kết nối an toàn
    public async disconnect(): Promise<void> {
        this.intentionalStop = true;
        this.clearRecoveryTimer();
        this.recoveryAttempt = 0;

        if (!this.connection) {
            this.isConnected = false;
            return;
        }

        // Nếu đang connecting, đợi hoàn thành
        if (this.isConnecting && this.connectPromise) {
            try {
                await this.connectPromise;
            } catch { /* ignore */ }
        }

        // Nếu đã disconnected, return
        if (this.connection.state === signalR.HubConnectionState.Disconnected) {
            this.isConnected = false;
            return;
        }

        // Nếu đang disconnecting, đợi hoàn thành
        if (this.isDisconnecting && this.disconnectPromise) {
            await this.disconnectPromise;
            return;
        }

        this.isDisconnecting = true;
        this.stopHeartbeat();

        this.disconnectPromise = (async () => {
            try {
                // Kiểm tra lại state
                if (this.connection?.state === signalR.HubConnectionState.Disconnected) {
                    this.isConnected = false;
                    return;
                }

                // Stop connection với timeout
                const stopPromise = this.connection?.stop();
                const timeoutPromise = new Promise<void>((_, reject) =>
                    setTimeout(() => reject(new Error('Stop timeout')), 5000)
                );

                try {
                    await Promise.race([stopPromise, timeoutPromise]);
                } catch (error: any) {
                    // Ignore timeout và các lỗi expected khi disconnect
                    if (!this.isDisconnectError(error)) {
                        console.warn('⚠️ SignalR disconnect warning:', error.message);
                    }
                }

                this.isConnected = false;

                // Notify handlers
                this.onConnectionStateChangedHandlers.forEach(handler => {
                    try { handler(false); } catch { /* ignore */ }
                });
            } catch (error: any) {
                // Luôn đặt isConnected = false
                this.isConnected = false;

                // Chỉ log nếu không phải lỗi expected
                if (!this.isDisconnectError(error)) {
                    console.warn('⚠️ Error during disconnect:', error.message);
                }
            } finally {
                this.isDisconnecting = false;
            }
        })();

        try {
            await this.disconnectPromise;
        } finally {
            this.disconnectPromise = null;
        }
    }

    // Helpers
    private wait(ms: number) {
        return new Promise(res => setTimeout(res, ms));
    }
    private async waitForState(target: signalR.HubConnectionState, timeoutMs: number) {
        const start = Date.now();
        while (this.connection && this.connection.state !== target) {
            if (Date.now() - start > timeoutMs) break;
            await this.wait(50);
        }
    }
    private async waitUntilNot(state: signalR.HubConnectionState, timeoutMs: number) {
        const start = Date.now();
        while (this.connection && this.connection.state === state) {
            if (Date.now() - start > timeoutMs) break;
            await this.wait(50);
        }
    }

    // 👥 Join/Leave Groups
    public async joinTicketGroup(ticketId: string): Promise<void> {
        try {
            await this.safeInvoke('JoinTicketGroup', ticketId);
            this.joinedTicketGroups.add(ticketId);
        } catch (error) {
            console.error('❌ Failed to join ticket group:', error);
            throw error;
        }
    }

    public async leaveTicketGroup(ticketId: string): Promise<void> {
        try {
            await this.safeInvoke('LeaveTicketGroup', ticketId);
            this.joinedTicketGroups.delete(ticketId);
        } catch (error) {
            console.error('Failed to leave ticket group:', error);
            throw error;
        }
    }

    // ChatHub methods
    public async sendPrivateMessage(toUserId: string, message: string): Promise<void> {
        await this.safeInvoke('SendPrivateMessage', toUserId, message);
    }

    public async joinGroup(groupName: string): Promise<void> {
        await this.safeInvoke('JoinGroup', groupName);
        this.joinedGroups.add(groupName);
    }

    public async leaveGroup(groupName: string): Promise<void> {
        await this.safeInvoke('LeaveGroup', groupName);
        this.joinedGroups.delete(groupName);
    }

    // 🏢 Tự động join tenant group
    private async autoJoinTenantGroup(tenantID: string): Promise<void> {
        if (!tenantID) {
            console.warn('⚠️ [SignalR] Cannot join tenant group: tenantID is empty');
            return;
        }

        const connectionId = this.connection?.connectionId;

        try {
            await this.safeInvoke('JoinGroup', tenantID);
            this.joinedGroups.add(tenantID);
        } catch (error) {
            console.error(`❌ [SignalR] Failed to join tenant group ${tenantID} (connectionId: ${connectionId}):`, error);
            throw error;
        }
    }

    public async sendMessageToGroup(groupName: string, message: string): Promise<void> {
        await this.safeInvoke('SendMessageToGroup', groupName, message);
    }

    public async sendMessage(message: string): Promise<void> {
        await this.safeInvoke('SendMessage', message);
    }

    /**
     * Gửi event đến group (tenant) - gọi trực tiếp từ client
     * @param groupName Tên group (thường là tenantID)
     * @param eventName Tên event (reloadEmployee, reloadDepartment, etc.)
     * @param data Data object để gửi
     */
    public async sendEventToGroup(groupName: string, eventName: string, data: any): Promise<void> {
        await this.safeInvoke('SendEventToGroup', groupName, eventName, data);
    }

    public async getOnlineUserConnections(): Promise<Record<string, string>> {
        const result = await this.safeInvoke<Record<string, string>>('GetOnlineUserConnections');
        return result || {};
    }

    // 🔥 THÊM: Đăng ký callback khi reconnect
    public onReconnect(callback: () => void): void {
        this.onReconnectCallbacks.add(callback);
    }

    public removeOnReconnect(callback: () => void): void {
        this.onReconnectCallbacks.delete(callback);
    }

    // 🎯 Setters cho event handler - chỉ giữ các handler có trong setupEventHandlers
    public setOnConnectionStateChanged(handler: (isConnected: boolean) => void) {
        this.onConnectionStateChangedHandlers.add(handler);
    }
    public removeOnConnectionStateChanged(handler: (isConnected: boolean) => void) {
        this.onConnectionStateChangedHandlers.delete(handler);
    }

    public setOnReloadEmployee(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadEmployee.add(handler);
    }
    public removeOnReloadEmployee(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadEmployee.delete(handler);
    }

    public setOnReloadDepartment(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadDepartment.add(handler);
    }
    public removeOnReloadDepartment(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadDepartment.delete(handler);
    }
    public setOnReloadPosition(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadPosition.add(handler);
    }
    public removeOnReloadPosition(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadPosition.delete(handler);
    }
    public setOnReloadRoleGroup(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadRoleGroup.add(handler);
    }
    public removeOnReloadRoleGroup(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadRoleGroup.delete(handler);
    }
    public setOnReloadPeriod(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadPeriod.add(handler);
    }
    public removeOnReloadPeriod(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadPeriod.delete(handler);
    }
    public setOnReloadUnit(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadUnit.add(handler);
    }
    public removeOnReloadUnit(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadUnit.delete(handler);
    }
    public setOnReloadRest(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadRest.add(handler);
    }
    public removeOnReloadRest(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadRest.delete(handler);
    }
    public setOnReloadOrgData(handler: (notification: SignalRNotificationMessage) => void) {
        console.log('[SignalR][setOnReloadOrgData] handler', handler);
        this.onReloadOrgData.add(handler);
    }
    public removeOnReloadOrgData(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadOrgData.delete(handler);
    }

    public setOnReloadDbRestore(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadDbRestore.add(handler);
        logDbRestoreTrace('2-FE-setOnReloadDbRestore', {
            handlerCount: this.onReloadDbRestore.size,
        });
    }
    public removeOnReloadDbRestore(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadDbRestore.delete(handler);
    }

    public setOnReloadCopyData(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadCopyData.add(handler);
        logCopyDataTrace('setOnReloadCopyData', { handlerCount: this.onReloadCopyData.size });
    }
    public removeOnReloadCopyData(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadCopyData.delete(handler);
    }

    public setOnReloadBulkEmployeeProgress(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadBulkEmployeeProgress.add(handler);
    }
    public removeOnReloadBulkEmployeeProgress(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadBulkEmployeeProgress.delete(handler);
    }

    public setOnFishbonePresence(handler: (payload: unknown) => void) {
        this.onFishbonePresence.add(handler);
    }
    public removeOnFishbonePresence(handler: (payload: unknown) => void) {
        this.onFishbonePresence.delete(handler);
    }

    public setOnFishboneUpdated(handler: (payload: unknown) => void) {
        this.onFishboneUpdated.add(handler);
    }
    public removeOnFishboneUpdated(handler: (payload: unknown) => void) {
        this.onFishboneUpdated.delete(handler);
    }

    public setOnStrategyMapPresence(handler: (payload: unknown) => void) {
        this.onStrategyMapPresence.add(handler);
    }
    public removeOnStrategyMapPresence(handler: (payload: unknown) => void) {
        this.onStrategyMapPresence.delete(handler);
    }
    public setOnDynamicEvent(eventName: string, handler: (payload: unknown) => void) {
        let handlers = this.dynamicEventHandlers.get(eventName);
        if (!handlers) {
            handlers = new Set();
            this.dynamicEventHandlers.set(eventName, handlers);
        }
        handlers.add(handler);
    }
    public removeOnDynamicEvent(eventName: string, handler: (payload: unknown) => void) {
        this.dynamicEventHandlers.get(eventName)?.delete(handler);
    }

    public setOnStrategyMapUpdated(handler: (payload: unknown) => void) {
        this.onStrategyMapUpdated.add(handler);
    }
    public removeOnStrategyMapUpdated(handler: (payload: unknown) => void) {
        this.onStrategyMapUpdated.delete(handler);
    }

    public setOnReloadFishboneSave(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadFishboneSave.add(handler);
    }
    public removeOnReloadFishboneSave(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadFishboneSave.delete(handler);
    }

    public setOnMessage(handler: (fromUserId: string, message: string) => void) {
        this.onMessageHandlers.add(handler);
    }
    public removeOnMessage(handler: (fromUserId: string, message: string) => void) {
        this.onMessageHandlers.delete(handler);
    }

    public setOnPrivateMessage(handler: (fromUserId: string, message: string) => void) {
        this.onPrivateMessageHandlers.add(handler);
    }
    public removeOnPrivateMessage(handler: (fromUserId: string, message: string) => void) {
        this.onPrivateMessageHandlers.delete(handler);
    }

    public setOnGroupMessage(handler: (groupName: string, fromUserId: string, message: string) => void) {
        this.onGroupMessageHandlers.add(handler);
    }
    public removeOnGroupMessage(handler: (groupName: string, fromUserId: string, message: string) => void) {
        this.onGroupMessageHandlers.delete(handler);
    }

    public setOnNotification(handler: (notification: SignalRNotificationMessage) => void) {
        this.onNotificationHandlers.add(handler);
    }
    public removeOnNotification(handler: (notification: SignalRNotificationMessage) => void) {
        this.onNotificationHandlers.delete(handler);
    }

    public setOnSystemMessage(handler: (message: string) => void) {
        this.onSystemMessageHandlers.add(handler);
    }
    public removeOnSystemMessage(handler: (message: string) => void) {
        this.onSystemMessageHandlers.delete(handler);
    }

    public setOnUserDisconnected(handler: (data: { userId: string; timestamp: string }) => void) {
        this.onUserDisconnectedHandlers.add(handler);
    }
    public removeOnUserDisconnected(handler: (data: { userId: string; timestamp: string }) => void) {
        this.onUserDisconnectedHandlers.delete(handler);
    }

    public setOnError(handler: (errorMessage: string) => void) {
        this.onErrorHandlers.add(handler);
    }
    public removeOnError(handler: (errorMessage: string) => void) {
        this.onErrorHandlers.delete(handler);
    }

    // Thread/Comment event handlers - nhận SignalRNotificationMessage giống ReceiveNotification
    public setOnThreadCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadCreatedHandlers.add(handler);
    }
    public removeOnThreadCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadCreatedHandlers.delete(handler);
    }

    public setOnThreadUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadUpdatedHandlers.add(handler);
    }
    public removeOnThreadUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadUpdatedHandlers.delete(handler);
    }

    public setOnThreadDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadDeletedHandlers.add(handler);
    }
    public removeOnThreadDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadDeletedHandlers.delete(handler);
    }

    public setOnThreadReactionUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadReactionUpdatedHandlers.add(handler);
    }
    public removeOnThreadReactionUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadReactionUpdatedHandlers.delete(handler);
    }

    public setOnCommentCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentCreatedHandlers.add(handler);
    }
    public removeOnCommentCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentCreatedHandlers.delete(handler);
    }

    public setOnCommentUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentUpdatedHandlers.add(handler);
    }
    public removeOnCommentUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentUpdatedHandlers.delete(handler);
    }

    public setOnCommentDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentDeletedHandlers.add(handler);
    }
    public removeOnCommentDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentDeletedHandlers.delete(handler);
    }

    public setOnCommentReactionUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentReactionUpdatedHandlers.add(handler);
    }
    public removeOnCommentReactionUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onCommentReactionUpdatedHandlers.delete(handler);
    }

    // ThreadPage event handlers
    public setOnThreadPageCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageCreatedHandlers.add(handler);
    }
    public removeOnThreadPageCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageCreatedHandlers.delete(handler);
    }

    public setOnThreadPageUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageUpdatedHandlers.add(handler);
    }
    public removeOnThreadPageUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageUpdatedHandlers.delete(handler);
    }

    public setOnThreadPageDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageDeletedHandlers.add(handler);
    }
    public removeOnThreadPageDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageDeletedHandlers.delete(handler);
    }

    public setOnThreadPageUserJoined(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageUserJoinedHandlers.add(handler);
    }
    public removeOnThreadPageUserJoined(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageUserJoinedHandlers.delete(handler);
    }

    public setOnThreadPageUserLeft(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageUserLeftHandlers.add(handler);
    }
    public removeOnThreadPageUserLeft(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageUserLeftHandlers.delete(handler);
    }

    public setOnThreadPageRequestApproved(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageRequestApprovedHandlers.add(handler);
    }
    public removeOnThreadPageRequestApproved(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageRequestApprovedHandlers.delete(handler);
    }

    public setOnThreadPageRequestRejected(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageRequestRejectedHandlers.add(handler);
    }
    public removeOnThreadPageRequestRejected(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageRequestRejectedHandlers.delete(handler);
    }

    public setOnThreadPageOwnershipTransferred(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageOwnershipTransferredHandlers.add(handler);
    }
    public removeOnThreadPageOwnershipTransferred(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPageOwnershipTransferredHandlers.delete(handler);
    }

    // ThreadPoll event handlers
    public setOnThreadPollCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollCreatedHandlers.add(handler);
    }
    public removeOnThreadPollCreated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollCreatedHandlers.delete(handler);
    }

    public setOnThreadPollUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollUpdatedHandlers.add(handler);
    }
    public removeOnThreadPollUpdated(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollUpdatedHandlers.delete(handler);
    }

    public setOnThreadPollDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollDeletedHandlers.add(handler);
    }
    public removeOnThreadPollDeleted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollDeletedHandlers.delete(handler);
    }

    public setOnThreadPollVoted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollVotedHandlers.add(handler);
    }
    public removeOnThreadPollVoted(handler: (notification: SignalRNotificationMessage) => void) {
        this.onThreadPollVotedHandlers.delete(handler);
    }

    public setOnReloadQuizIframe(handler: (notification: SignalRNotificationMessage) => void) {
        this.onQuizHandlers.add(handler);
    }
    public removeOnReloadQuizIframe(handler: (notification: SignalRNotificationMessage) => void) {
        this.onQuizHandlers.delete(handler);
    }

    // License reload handlers
    public setOnReloadLicense(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadLicense.add(handler);
    }
    public removeOnReloadLicense(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadLicense.delete(handler);
    }

    // Config reload handlers
    public setOnReloadConfig(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadConfig.add(handler);
    }
    public removeOnReloadConfig(handler: (notification: SignalRNotificationMessage) => void) {
        this.onReloadConfig.delete(handler);
    }

    // 🔄 Getters
    public getConnectionState(): signalR.HubConnectionState | null {
        return this.connection?.state || null;
    }

    public isConnectionActive(): boolean {
        return this.connection !== null &&
            this.connection.state === signalR.HubConnectionState.Connected &&
            this.isConnected === true;
    }

    public getConnectionId(): string | null {
        return this.connection?.connectionId || null;
    }

    // Helper method để xử lý lỗi disconnect một cách an toàn
    private isDisconnectError(error: any): boolean {
        if (!error) return false;

        const errorMessage = error.message?.toLowerCase() || '';
        const errorName = error.name?.toLowerCase() || '';
        const errorCode = error.code?.toLowerCase() || '';

        return (
            errorMessage.includes('invocation canceled') ||
            errorMessage.includes('connection being closed') ||
            errorMessage.includes('underlying connection being closed') ||
            errorMessage.includes('stop timeout') ||
            errorMessage.includes('no connection with that id') ||
            errorMessage.includes('status code \'404\'') ||
            errorName === 'aborterror' ||
            errorCode === 'err_canceled' ||
            errorCode === 'cancelled'
        );
    }

    // 🔄 Safe invoke với auto-retry và connection check
    private async safeInvoke<T = void>(
        methodName: string,
        ...args: any[]
    ): Promise<T> {
        if (!this.connection) {
            throw new Error('SignalR connection is not initialized');
        }

        // Kiểm tra connection state
        const currentState = this.connection.state;
        if (currentState !== signalR.HubConnectionState.Connected) {
            // Thử reconnect nếu đang disconnected
            if (currentState === signalR.HubConnectionState.Disconnected) {
                console.warn(`⚠️ Connection disconnected, attempting to reconnect before ${methodName}...`);
                try {
                    await this.connect();
                } catch {
                    throw new Error(`Cannot invoke ${methodName}: Connection failed to reconnect`);
                }
            } else {
                throw new Error(`Cannot invoke ${methodName}: Connection state is ${currentState}`);
            }
        }

        // Thực hiện invoke với retry
        let lastError: any = null;
        const maxRetries = 2;

        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                if (attempt > 0) {
                    // Đợi một chút trước khi retry
                    await this.wait(500 * attempt);
                }

                // Kiểm tra lại state trước mỗi lần invoke
                if (this.connection.state !== signalR.HubConnectionState.Connected) {
                    if (attempt < maxRetries) {
                        await this.connect();
                        continue;
                    }
                    throw new Error(`Connection lost during ${methodName}`);
                }

                const result = await this.connection.invoke<T>(methodName, ...args);
                return result;
            } catch (error: any) {
                lastError = error;

                // Nếu là lỗi connection, thử reconnect
                if (this.isDisconnectError(error) ||
                    error.message?.includes('No Connection') ||
                    error.message?.includes('404')) {

                    if (attempt < maxRetries) {
                        console.warn(`⚠️ Connection error in ${methodName}, attempting reconnect...`);
                        this.isConnected = false;
                        try {
                            await this.connect();
                            continue; // Retry invoke
                        } catch (reconnectError) {
                            throw new Error(`Failed to reconnect: ${reconnectError}`);
                        }
                    }
                }

                // Nếu không phải lỗi connection hoặc đã hết retry, throw error
                if (attempt === maxRetries) {
                    break;
                }
            }
        }

        throw lastError || new Error(`Failed to invoke ${methodName} after ${maxRetries + 1} attempts`);
    }

    // 🔄 Restart connection safely
    public async restart(): Promise<void> {
        try {
            await this.disconnect();
            await this.connect();
        } catch (error) {
            console.error('❌ Failed to restart SignalR connection:', error);
            throw error;
        }
    }

    // 🛡️ Safe disconnect for page navigation
    public async safeDisconnect(): Promise<void> {
        try {
            // Chỉ disconnect nếu đang connected
            if (this.isConnected && this.connection) {
                await this.disconnect();
            }
        } catch (error: any) {
            // Ignore tất cả lỗi khi chuyển trang
            if (this.isDisconnectError(error)) {
                console.error('🛡️ Safe disconnect completed with expected error');
            } else {
                console.error('🛡️ Safe disconnect completed with unexpected error (ignored):', error.message);
            }
        }
    }

    // SignalR already sends protocol-level keep-alive pings. Do not invoke a
    // custom hub method here because not every backend exposes UpdateActivity.
    private startHeartbeat() {
        this.stopHeartbeat();
    }

    private stopHeartbeat() {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }

    private clearRecoveryTimer() {
        if (this.recoveryTimer) {
            clearTimeout(this.recoveryTimer);
            this.recoveryTimer = null;
        }
    }

    private ensureOnlineRecoveryListener() {
        if (typeof window === 'undefined' || this.onlineListenerAttached) return;
        window.addEventListener('online', this.handleBrowserOnline);
        this.onlineListenerAttached = true;
    }

    private handleBrowserOnline = () => {
        void this.tryRecoverConnection('browser-online');
    };

    /** Backoff dài sau khi withAutomaticReconnect trả null (30s → max 5 phút). */
    private scheduleRecoveryReconnect(delayMs?: number) {
        if (this.intentionalStop) return;
        if (!useAuthStore.getState().accessToken) return;

        this.clearRecoveryTimer();
        const delay =
            delayMs ??
            Math.min(30000 * Math.pow(2, this.recoveryAttempt), 5 * 60 * 1000);

        console.warn(`[SignalR] Recovery reconnect scheduled in ${delay}ms (attempt ${this.recoveryAttempt})`);
        this.recoveryTimer = setTimeout(() => {
            void this.tryRecoverConnection('scheduled');
        }, delay);
    }

    private async tryRecoverConnection(reason: string): Promise<void> {
        if (this.intentionalStop) return;
        if (!useAuthStore.getState().accessToken) return;
        if (this.isConnecting || this.isDisconnecting) return;
        if (this.isConnectionActive()) {
            this.recoveryAttempt = 0;
            this.clearRecoveryTimer();
            return;
        }

        console.warn(`[SignalR] Recovery reconnect (${reason})`);
        try {
            await this.connect();
            this.recoveryAttempt = 0;
            this.clearRecoveryTimer();
        } catch (error) {
            console.error('[SignalR] Recovery reconnect failed:', error);
            this.recoveryAttempt += 1;
            this.scheduleRecoveryReconnect();
        }
    }

    // 🔥 THÊM: Method để kiểm tra handlers có sẵn sàng không
    public hasHandlersRegistered(): boolean {
        return this.onThreadCreatedHandlers.size > 0 ||
            this.onNotificationHandlers.size > 0 ||
            this.onReloadEmployee.size > 0;
    }

    // 🔥 THÊM: Method để debug handler counts
    public getHandlerCounts(): Record<string, number> {
        return {
            onThreadCreatedHandlers: this.onThreadCreatedHandlers.size,
            onThreadUpdatedHandlers: this.onThreadUpdatedHandlers.size,
            onCommentCreatedHandlers: this.onCommentCreatedHandlers.size,
            onNotificationHandlers: this.onNotificationHandlers.size,
            onReloadEmployee: this.onReloadEmployee.size,
            onConnectionStateChangedHandlers: this.onConnectionStateChangedHandlers.size,
            onReconnectCallbacks: this.onReconnectCallbacks.size,
            onQuizHandlers: this.onQuizHandlers.size,
        };
    }
}

// 🧠 Singleton instance
export const signalRService = new SignalRService();
export default signalRService;
